import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load saved customer on mount
  useEffect(() => {
    const token = localStorage.getItem('customer_auth_token');
    const saved = localStorage.getItem('customer_data');
    if (token && saved) {
      try {
        const parsed = JSON.parse(saved);
        setCustomer(parsed);
        setIsAuthenticated(true);
        apiService.setToken(token);
      } catch (e) {
        // ignore
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback((token, customerData) => {
    apiService.setToken(token);
    localStorage.setItem('customer_data', JSON.stringify(customerData));
    setCustomer(customerData);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (e) {}
    apiService.clearToken();
    // Clear ops/brand session so they go back to gateway login
    localStorage.removeItem('biryani_ops_autologin');
    sessionStorage.removeItem('basicAuthAuthenticated');
    sessionStorage.removeItem('basicAuthUser');
    sessionStorage.removeItem('opsAccessToken');
    sessionStorage.removeItem('opsCustomerData');
    sessionStorage.removeItem('gatewayBrand');      // clear brand context
    setCustomer(null);
    setIsAuthenticated(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await apiService.getProfile();
      if (res.success && res.data) {
        const profile = res.data.customer || res.data;
        setCustomer(profile);
        localStorage.setItem('customer_data', JSON.stringify(profile));
        return profile;
      }
    } catch (e) {}
    return null;
  }, []);

  const updateCustomerData = useCallback((data) => {
    setCustomer(prev => {
      const updated = { ...prev, ...data };
      localStorage.setItem('customer_data', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        customer,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshProfile,
        updateCustomerData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
